import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import useCurrentDragType from "@editor/hooks/useCurrentDragType";

import Popover from "@replo/design-system/components/popover/Popover";
import twMerge from "@replo/design-system/utils/twMerge";
import { useRequiredContext } from "replo-utils/react/context";

import { AssetCardActions } from "./asset-card/AssetCardActions";
import { AssetPreview } from "./asset-card/AssetPreview";
import { AssetThumbnail } from "./asset-card/AssetThumbnail";
import AssetCardDragOverlay from "./AssetCardDragOverlay";

export type AssetCardProps = {
  asset: Asset;
  onClick?: () => void;
  shouldAllowActions?: boolean;
  shouldAllowDrag?: boolean;
  isUploading?: boolean;
  className?: string;
};

type AssetCardAnchorProps = "onClick" | "shouldAllowDrag" | "className";

const AssetCardContext = React.createContext<{
  isAssetPreviewLoaded: boolean;
  setIsAssetPreviewLoaded: (isAssetPreviewLoaded: boolean) => void;
  asset: Asset;
  handlePopoverOpenChange: (isOpen: boolean) => void;
  anchorRef: React.RefObject<HTMLDivElement>;
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  setActionPopoverContent: (
    actionPopoverContent: "delete" | "rename" | null,
  ) => void;
  actionPopoverContent: "delete" | "rename" | null;
  isUploading?: boolean;
} | null>(null);

const useAssetCardContext = () => useRequiredContext(AssetCardContext);

const AssetCardRoot: React.FC<
  React.PropsWithChildren<Omit<AssetCardProps, AssetCardAnchorProps>>
> = (props) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isAssetPreviewLoaded, setIsAssetPreviewLoaded] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [actionPopoverContent, setActionPopoverContent] = React.useState<
    "delete" | "rename" | null
  >(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handlePopoverOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen);
    if (!isOpen) {
      setIsAssetPreviewLoaded(false);
    }
  };

  // NOTE (Max, 2025-09-23): Close popover if anchor is not intersecting (hidden due to overflow)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Close popover when anchor element is not visible due to overflow
  React.useEffect(() => {
    if (!anchorRef.current) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry && !entry.isIntersecting && isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    });

    observer.observe(anchorRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isPopoverOpen]);

  const contextValue = {
    isAssetPreviewLoaded,
    setIsAssetPreviewLoaded,
    asset: props.asset,
    handlePopoverOpenChange,
    anchorRef,
    isMenuOpen,
    setIsMenuOpen,
    setActionPopoverContent,
    actionPopoverContent,
    isUploading: props.isUploading,
  };

  return (
    <AssetCardContext.Provider value={contextValue}>
      <Popover.Root
        isOpen={isPopoverOpen}
        onOpenChange={handlePopoverOpenChange}
      >
        {props.children}
      </Popover.Root>
    </AssetCardContext.Provider>
  );
};

const AssetCardAnchor: React.FC<
  React.PropsWithChildren<Pick<AssetCardProps, AssetCardAnchorProps>>
> = ({ children, shouldAllowDrag = true, onClick }) => {
  const {
    anchorRef,
    handlePopoverOpenChange,
    asset,
    isMenuOpen,
    actionPopoverContent,
    isUploading,
  } = useAssetCardContext();

  const { currentDragIdentifier } = useCurrentDragType();
  const isDragging = currentDragIdentifier !== null;

  return (
    <Popover.Anchor
      ref={anchorRef}
      className={twMerge(
        "border-0.5 border-border rounded-md p-1.5 flex flex-col gap-1 h-40",
        onClick && "cursor-pointer",
      )}
      onMouseEnter={() => {
        if (
          !isDragging &&
          !isMenuOpen &&
          actionPopoverContent === null &&
          !isUploading
        ) {
          handlePopoverOpenChange(true);
        }
      }}
      onMouseLeave={() => {
        handlePopoverOpenChange(false);
      }}
      onClick={onClick}
    >
      <AssetCardDragOverlay
        asset={{
          id: asset.id,
          type: asset.type,
          url: asset.url,
          altText: asset.altText ?? "",
        }}
        shouldAllowDrag={shouldAllowDrag && !isUploading}
      >
        <AssetThumbnail asset={asset} isUploading={isUploading} />
      </AssetCardDragOverlay>
      {children}
    </Popover.Anchor>
  );
};

const AssetCardContent: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ children, className }) => {
  const { asset, isAssetPreviewLoaded, setIsAssetPreviewLoaded } =
    useAssetCardContext();

  return (
    <Popover.Content
      onOpenAutoFocus={(event) => {
        event.preventDefault();
      }}
      shouldPreventDefaultOnInteractOutside={false}
      hideCloseButton
      side="right"
      align="start"
      sideOffset={4}
      avoidCollisions
      collisionPadding={30}
      className={twMerge(
        "pointer-events-none isolation-auto flex flex-col gap-1 rounded-md opacity-0 w-[150px] md:w-[350px] lg:w-[450px]",
        // NOTE (Gabe 2025-03-03): We use this trick to prevent layout shift
        // since the popover dimensions are based on the loaded image/video.
        isAssetPreviewLoaded && "opacity-100",
        className,
      )}
    >
      <AssetPreview
        asset={asset}
        onLoad={() => setIsAssetPreviewLoaded(true)}
      />
      {children}
    </Popover.Content>
  );
};

const AssetCardAnchorFooter: React.FC<{
  shouldAllowActions?: boolean;
}> = ({ shouldAllowActions = true }) => {
  const {
    asset,
    isMenuOpen,
    setIsMenuOpen,
    setActionPopoverContent,
    actionPopoverContent,
    isUploading,
  } = useAssetCardContext();

  return (
    <div className="relative flex min-h-6 items-center justify-between gap-1">
      <span className="block truncate text-sm font-medium">
        {asset.name}
        {asset.fileExtension ? `.${asset.fileExtension}` : ""}
      </span>
      {shouldAllowActions && !isUploading && (
        <AssetCardActions
          className={twMerge(
            "invisible w-0 group-hover/asset-card:visible group-hover/asset-card:w-auto",
            isMenuOpen && "visible w-auto",
          )}
          asset={asset}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          setActionPopoverContent={setActionPopoverContent}
          actionPopoverContent={actionPopoverContent}
        />
      )}
    </div>
  );
};

export const AssetCard = (props: AssetCardProps) => {
  return (
    <AssetCardRoot {...props}>
      <AssetCardAnchor
        className={twMerge(props.className, "p-0.5")}
        shouldAllowDrag={props.shouldAllowDrag}
        onClick={props.onClick}
      />
      <AssetCardContent className="p-0.5" />
    </AssetCardRoot>
  );
};

AssetCard.Root = AssetCardRoot;
AssetCard.Anchor = AssetCardAnchor;
AssetCard.Content = AssetCardContent;
AssetCard.AnchorFooter = AssetCardAnchorFooter;
